import { styled } from '@mui/material/styles'
import React, { ReactNode, useState } from 'react'
import { Doughnut } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import InfoBlock from '../../components/InfoBlock'

import { Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'

import { useTheme } from '@mui/material/styles'
import { formatBigMoney, formatInteger, formatPercentage } from '../../../packages/utils/numberFormatters'
import * as sharesOrUnits from '../../../utils/issues/sharesOrUnits'
import { getLevelName, calculateIpoOutcome } from '../utils'

const ChartWrapper = styled('div')`
    height: 200px;
    position: relative;
    margin: 20px;
`

const DoughnutInner = styled('div')`
    width: 100%;
    position: absolute;
    top: 50%;
    left: 0;
    margin-top: -22px;
    text-align: center;
    z-index: 0;
`

function EmptyBox({ color, side = null }) {
    const sideLength = side || 30
    return <div style={{ width: sideLength, height: sideLength, backgroundColor: color }}></div>
}

export const IpoOutcomeOverview = ({ issue }) => {
    const [showDetailedGuarantorView, setShowDetailedGuarantorView] = useState(false)
    const { t, i18n } = useTranslation()
    const locale = i18n.language
    const theme = useTheme()

    let content: ReactNode | null = null

    const colors = {
        pctSubCommitment: theme.palette.success.main,
        pctSubscribedPublic: theme.palette.success.light,
        pctSubscribedGuarantors: theme.palette.warning.main,
        pctNotSubscribed: theme.palette.grey[200],
    }

    if (issue) {
        const outcome = calculateIpoOutcome(issue)

        const data = {
            labels: [
                t('pages.issues.outcome.chartBySubscribers'),
                t('pages.issues.outcome.chartByPublic'),
                t('pages.issues.outcome.chartByGuarantors'),
                t('pages.issues.outcome.chartNotSubscribed'),
            ],
            datasets: [
                {
                    data: [
                        outcome.pctSubCommitment,
                        outcome.pctSubscribedPublic,
                        outcome.pctSubscribedGuarantors,
                        outcome.pctNotSubscribed,
                    ],
                    backgroundColor: [
                        colors.pctSubCommitment,
                        colors.pctSubscribedPublic,
                        colors.pctSubscribedGuarantors,
                        colors.pctNotSubscribed,
                    ],
                    borderWidth: 1,
                    borderColor: theme.palette.grey[400],
                },
            ],
        }
        const options = {
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        label: (item) => formatPercentage(item.parsed, { locale, decimals: 1 }),
                    },
                },
            },
            cutout: '70%',
        }
        const formatSubVolume = (subVolume) => {
            const number = formatInteger(subVolume, { locale })
            return number
        }

        const maxFeeLevel = Math.max(...issue.fees.map((level) => level.feeLevel))

        const bodyRows: ReactNode[] = []
        if (outcome.pctSubCommitment > 0 || outcome.unitSubCommitment > 0) {
            bodyRows.push(
                <TableRow key={'pctSubCommitment'}>
                    <TableCell>
                        <EmptyBox color={colors.pctSubCommitment} />
                    </TableCell>
                    <TableCell>{t('pages.issues.outcome.bySubscribers')}</TableCell>
                    <TableCell>{formatPercentage(outcome.pctSubCommitment, { locale, decimals: 0 })}</TableCell>
                    <TableCell>{formatSubVolume(outcome.unitSubCommitment)}</TableCell>
                    <TableCell>{formatBigMoney(issue.unitSubPrice * outcome.unitSubCommitment, { locale })}</TableCell>
                </TableRow>,
            )
        }
        if (outcome.pctSubscribedPublic > 0 || outcome.unitSubPublic > 0) {
            bodyRows.push(
                <TableRow key={'pctSubscribedPublic'}>
                    <TableCell>
                        <EmptyBox color={colors.pctSubscribedPublic} />
                    </TableCell>
                    <TableCell>{t('pages.issues.outcome.byPublic')}</TableCell>
                    <TableCell>{formatPercentage(outcome.pctSubscribedPublic, { locale, decimals: 0 })}</TableCell>
                    <TableCell>{formatSubVolume(outcome.unitSubPublic)}</TableCell>
                    <TableCell>{formatBigMoney(issue.unitSubPrice * outcome.unitSubPublic, { locale })}</TableCell>
                </TableRow>,
            )
        }
        if (outcome.pctSubscribedGuarantors > 0 || outcome.unitSubGuarantor > 0) {
            bodyRows.push(
                <TableRow
                    key={'pctSubscribedGuarantors'}
                    sx={showDetailedGuarantorView ? { '.MuiTableCell-root': { borderBottom: 'none' } } : null}
                >
                    <TableCell>
                        <EmptyBox color={colors.pctSubscribedGuarantors} />
                    </TableCell>
                    <TableCell>
                        <Stack direction='column'>
                            <Typography>{t('pages.issues.outcome.byGuarantors')}</Typography>
                            {outcome.detailedGuarantorData.length > 1 ? (
                                <Button
                                    onClick={() => setShowDetailedGuarantorView(!showDetailedGuarantorView)}
                                    sx={{ padding: 0, justifyContent: 'left', textAlign: 'left' }}
                                >
                                    {showDetailedGuarantorView
                                        ? t('site.actions.hideDetailed')
                                        : t('site.actions.showDetailed')}
                                </Button>
                            ) : null}
                        </Stack>
                    </TableCell>
                    <TableCell>{formatPercentage(outcome.pctSubscribedGuarantors, { locale, decimals: 0 })}</TableCell>
                    <TableCell>{formatSubVolume(outcome.unitSubGuarantor)}</TableCell>
                    <TableCell>{formatBigMoney(issue.unitSubPrice * outcome.unitSubGuarantor, { locale })}</TableCell>
                </TableRow>,
            )

            if (showDetailedGuarantorView && outcome.detailedGuarantorData.length > 1) {
                bodyRows.push(
                    ...outcome.detailedGuarantorData.map((level, index) => (
                        <TableRow
                            key={level.feeLevel}
                            sx={
                                outcome.detailedGuarantorData.length - 1 !== index
                                    ? { td: { borderBottom: 'none', paddingY: 0 } }
                                    : { td: { paddingTop: 0 } }
                            }
                        >
                            <TableCell />
                            <TableCell>{getLevelName(level.feeLevel, maxFeeLevel)}</TableCell>
                            <TableCell>{formatPercentage(level.pctOfIssue * 100, { locale, decimals: 0 })}</TableCell>
                            <TableCell>{formatSubVolume(level.numberOfUnits)}</TableCell>
                            <TableCell>
                                {formatBigMoney(level.amount, { locale })} (
                                {formatPercentage(level.pctOfGuarantSub * 100, { locale, decimals: 0 })})
                            </TableCell>
                        </TableRow>
                    )),
                )
            }
        } else if (!issue.noGuarantors) {
            bodyRows.push(
                <TableRow key={'pctSubscribedGuarantors'}>
                    <TableCell>
                        <EmptyBox color={colors.pctSubscribedGuarantors} />
                    </TableCell>
                    <TableCell>{t('pages.issues.outcome.byGuarantors')}</TableCell>
                    <TableCell>{formatPercentage(outcome.pctSubscribedGuarantors, { locale, decimals: 0 })}</TableCell>
                    <TableCell>{formatSubVolume(outcome.unitSubGuarantor)}</TableCell>
                    <TableCell>-</TableCell>
                </TableRow>,
            )
        }

        if (issue.overAllotmentSize > 0 || issue.overAllotmentSizeSecondary > 0) {
            const outcomeNotReady =
                issue.overAllotmentAllocation === null && issue.overAllotmentAllocationSecondary === null
            bodyRows.push(
                <TableRow key={'overAllotment'}>
                    <TableCell />
                    <TableCell>{t('pages.issues.outcome.overAllotment')}</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>{outcomeNotReady ? '-' : formatSubVolume(outcome.overAllotment)}</TableCell>
                    <TableCell>
                        {outcome.overAllotment === 0 || outcomeNotReady
                            ? '-'
                            : formatBigMoney(issue.unitSubPrice * outcome.overAllotment, { locale })}
                    </TableCell>
                </TableRow>,
            )
        }

        content = (
            <>
                <ChartWrapper>
                    <DoughnutInner>
                        <Typography variant='h4'>
                            {formatPercentage(100 - outcome.pctNotSubscribed, { locale, decimals: 0 })}
                        </Typography>
                        <Typography variant='caption'>{t('pages.issues.outcome.subscriptionRate')}</Typography>
                    </DoughnutInner>
                    <Doughnut data={data} options={options} />
                </ChartWrapper>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell>{t('pages.issues.outcome.subscribed')}</TableCell>
                            <TableCell>{t('pages.issues.outcome.percent')}</TableCell>
                            <TableCell>
                                {sharesOrUnits.isUnit(issue)
                                    ? t('pages.issues.outcome.countUnits')
                                    : t('pages.issues.outcome.countShares')}
                            </TableCell>
                            <TableCell>{t('pages.issues.outcome.amount')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {bodyRows}
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell />
                            <TableCell>{t('site.table.total')}</TableCell>
                            <TableCell>
                                {formatPercentage(
                                    outcome.pctSubCommitment +
                                        outcome.pctSubscribedPublic +
                                        outcome.pctSubscribedGuarantors,
                                    { decimals: 0, locale },
                                )}
                            </TableCell>
                            <TableCell>
                                {formatInteger(
                                    outcome.unitSubCommitment +
                                        outcome.unitSubPublic +
                                        outcome.unitSubGuarantor +
                                        outcome.overAllotment,
                                    { locale },
                                )}
                            </TableCell>
                            <TableCell>
                                {formatBigMoney(
                                    issue.unitSubPrice *
                                        (outcome.unitSubCommitment +
                                            outcome.unitSubPublic +
                                            outcome.unitSubGuarantor +
                                            outcome.overAllotment),
                                    { locale },
                                )}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </>
        )
    }

    return (
        <InfoBlock title={t('pages.issues.outcome.title')} loadingElement={!content}>
            {content}
        </InfoBlock>
    )
}
